import * as actions from './actionTypes';
import firebase from '../../Firebase/Firebase';
import { getFirestore } from 'redux-firestore';
import { approximatePublicLocation, makeGeoLocation } from '../../Utils/geo';
import { privateDemandLocationRef } from '../../Utils/demandLocation';
import { deleteDemandSecurely, listAvailableDemands, listPublicReviewedProjects } from '../../Services/demandLifecycle';

const safeFileName = (name = 'image') =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();

const errorMessage = (error) => {
  const message = String(error?.message || '').trim();
  const code = String(error?.code || '').toLowerCase();
  if (message && !/^internal$/i.test(message) && !code.includes('internal')) return message;
  if (code.includes('unavailable')) return 'Le service est momentanément indisponible. Réessayez dans quelques instants.';
  return 'Impossible de charger ces informations pour le moment. Rechargez la page après le déploiement des fonctions.';
};

export const setLoading = (status) => ({ type: actions.SET_LOADING_STATUS, status });
export const getDemands = (payload) => ({ type: actions.GET_DEMANDS, payload });

export const deleteDemand = (demandId) => async (dispatch) => {
  dispatch({ type: actions.DELETE_DEMAND_START });
  try {
    await deleteDemandSecurely({ demandId });
    dispatch({ type: actions.DELETE_DEMAND_SUCCESS, payload: demandId });
    return true;
  } catch (error) {
    dispatch({ type: actions.DELETE_DEMAND_FAIL, payload: errorMessage(error) });
    return false;
  }
};

export const addDemandToDbAPI = (payload) => async (dispatch) => {
  const storage = firebase.storage();
  const db = getFirestore();
  const demandRef = db.collection('demands').doc();
  const images = Array.isArray(payload?.demandImages) ? payload.demandImages : [];
  const uploadedRefs = [];

  dispatch({ type: actions.ADD_DEMAND_START });
  dispatch(setLoading(true));

  try {
    if (!payload || !payload.clientId) throw new Error('Votre session client est invalide.');

    const uploadPromises = images.map(async (image, index) => {
      const storagePath = [
        'demandImages',
        payload.clientId,
        demandRef.id,
        `${Date.now()}-${index}-${safeFileName(image.name)}`,
      ].join('/');
      const imageRef = storage.ref().child(storagePath);
      await imageRef.put(image);
      uploadedRefs.push(imageRef);
      return imageRef.getDownloadURL();
    });

    const fileDownloadURLs = await Promise.all(uploadPromises);

    const exactLocation = makeGeoLocation({
      ...(payload.exactLocation || {}),
      formattedAddress: payload.address || payload.exactLocation?.formattedAddress || '',
    });
    const publicLocation = exactLocation ? approximatePublicLocation(exactLocation) : null;
    const publicAddress = publicLocation?.label || payload.address || '';

    const batch = db.batch();
    batch.set(demandRef, {
      demand_id: demandRef.id,
      user_id: payload.clientId,
      user_email_address: payload.userEmail || '',
      user_firstname: payload.userFirstName || '',
      user_lastname: payload.userLastName || '',
      client_profile_image: payload.clientProfileImage || '',
      client_trust_score: Number(payload.clientTrustScore || 0),
      client_average_rating: Number(payload.clientAverageRating || 0),
      client_verified_projects: Number(payload.clientVerifiedProjects || 0),
      date: firebase.firestore.FieldValue.serverTimestamp(),
      demand_status: 'unassigned',
      demand_image: fileDownloadURLs,
      description: payload.description || '',
      demand_type: payload.demandType || '',
      client_phone_number: String(payload.contactNumber || '').trim(),
      demand_address: publicAddress,
      public_location: publicLocation || null,
      location_enabled: Boolean(exactLocation),
      client_address: payload.clientAddress || '',
      demand_region: payload.demandRegion || '',
      public_preview_consent: payload.publicPreviewConsent === true,
      schema_version: '2026-08-two-root-v2',
      created_at: firebase.firestore.FieldValue.serverTimestamp(),
      updated_at: firebase.firestore.FieldValue.serverTimestamp(),
    });

    if (exactLocation) {
      const privateLocationRef = privateDemandLocationRef(db, demandRef.id);
      batch.set(privateLocationRef, {
        demand_id: demandRef.id,
        client_id: payload.clientId,
        allowed_artisan_id: '',
        exact_address: exactLocation.formattedAddress || payload.address || '',
        latitude: exactLocation.latitude,
        longitude: exactLocation.longitude,
        geohash: exactLocation.geohash,
        place_id: exactLocation.placeId || '',
        city: exactLocation.city || '',
        postal_code: exactLocation.postalCode || '',
        created_at: firebase.firestore.FieldValue.serverTimestamp(),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }

    await batch.commit();

    dispatch({ type: actions.ADD_DEMAND_SUCCESS });
    return demandRef.id;
  } catch (error) {
    await Promise.allSettled(uploadedRefs.map((imageRef) => imageRef.delete()));
    dispatch({ type: actions.ADD_DEMAND_FAIL, payload: errorMessage(error) });
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export function getDemandsAPI() {
  return (dispatch, getState) => {
    const db = getFirestore();
    const userId = getState().firebase.auth.uid || firebase.auth().currentUser?.uid;

    if (!userId) {
      dispatch(getDemands([]));
      dispatch(setLoading(false));
      return () => undefined;
    }

    dispatch(setLoading(true));
    const query = db
      .collection('demands')
      .where('user_id', '==', userId)
      .limit(300);

    return query.onSnapshot(
      (snapshot) => {
        const projects = snapshot.docs
          .map((doc) => ({ ...doc.data(), demand_id: doc.id }))
          .sort((a, b) => timestampValue(b.updated_at || b.date) - timestampValue(a.updated_at || a.date));
        dispatch(getDemands(projects));
        dispatch(setLoading(false));
      },
      (error) => {
        dispatch({ type: actions.GET_DEMANDS_FAIL, payload: errorMessage(error) });
        dispatch(setLoading(false));
      },
    );
  };
}

const timestampValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (typeof value?.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const getAvailableDemandsForArtisanAPI = () => async (dispatch, getState) => {
  const profile = getState().firebase.profile || {};
  if (profile.user_type !== 'Artisan' || !profile.speciality) {
    dispatch(getDemands([]));
    dispatch(setLoading(false));
    return () => undefined;
  }

  dispatch(setLoading(true));
  try {
    const available = await listAvailableDemands();
    dispatch(getDemands(available));
  } catch (error) {
    dispatch({ type: actions.GET_DEMANDS_FAIL, payload: errorMessage(error) });
  } finally {
    dispatch(setLoading(false));
  }
  return () => undefined;
};

export const getDemandsForArtisanAPI = () => (dispatch, getState) => {
  const db = getFirestore();
  const profile = getState().firebase.profile || {};
  const artisanId = getState().firebase.auth.uid || firebase.auth().currentUser?.uid;

  if (profile.user_type !== 'Artisan' || !artisanId) {
    dispatch(getDemands([]));
    dispatch(setLoading(false));
    return () => undefined;
  }

  dispatch(setLoading(true));

  // Missions belong to the selected artisan, regardless of the administrative
  // region saved in their profile. This is essential near regional borders and
  // after geolocation-based matching. No composite index is required.
  const query = db
    .collection('demands')
    .where('estimation.artisan_id', '==', artisanId)
    .limit(300);

  return query.onSnapshot(
    (snapshot) => {
      const projects = snapshot.docs
        .map((doc) => ({ ...doc.data(), demand_id: doc.id }))
        .sort((a, b) => timestampValue(b.updated_at || b.date) - timestampValue(a.updated_at || a.date));
      dispatch(getDemands(projects));
      dispatch(setLoading(false));
    },
    (error) => {
      dispatch({ type: actions.GET_DEMANDS_FAIL, payload: errorMessage(error) });
      dispatch(setLoading(false));
    },
  );
};

export function getReviewedDemandsAPI() {
  return async (dispatch) => {
    dispatch(setLoading(true));
    try {
      const projects = await listPublicReviewedProjects();
      dispatch(getDemands(projects));
    } catch (error) {
      dispatch({ type: actions.GET_DEMANDS_FAIL, payload: errorMessage(error) });
    } finally {
      dispatch(setLoading(false));
    }
    return () => undefined;
  };
}
